/**
 * Supplier Performance Scorecard — Phase P3-9.
 *
 * Single-supplier deep-dive. Composite score + metrics + radar +
 * trend charts + history tables + rule-based insights.
 *
 * Route: /admin/procurement/suppliers/:id/score
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Award, TrendingUp, AlertTriangle, Clock,
  CheckCircle2, DollarSign, Package, FileText, ExternalLink,
  Lightbulb, XCircle,
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import BackButton from '../../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRADE_COLOURS = {
  A: { bg: '#059669', text: '#FFFFFF', label: 'Excellent' },
  B: { bg: '#16A34A', text: '#FFFFFF', label: 'Strong' },
  C: { bg: '#D97706', text: '#FFFFFF', label: 'Acceptable' },
  D: { bg: '#DC2626', text: '#FFFFFF', label: 'At risk' },
  E: { bg: '#7F1D1D', text: '#FFFFFF', label: 'Critical' },
};

const RISK_COLOURS = {
  low: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  high: 'bg-red-100 text-red-800 border-red-200',
};

const fmtSpend = (spendDict) => {
  const entries = Object.entries(spendDict || {});
  if (!entries.length) return '—';
  return entries.map(([ccy, v]) => `${ccy} ${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`).join(' · ');
};

const KPICard = ({ icon: Icon, label, value, sublabel, color, testId }) => (
  <div
    className="rounded-lg bg-white border border-slate-200 p-4"
    style={{ borderLeftColor: color, borderLeftWidth: 3 }}
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className="mt-1 text-2xl font-bold truncate" style={{ color }}>{value}</p>
        {sublabel && <p className="mt-0.5 text-[11px] text-slate-500">{sublabel}</p>}
      </div>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md" style={{ backgroundColor: `${color}15`, color }}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, children, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <h3 className="text-sm font-semibold text-slate-800 mb-3">{title}</h3>
      <div className="min-h-[220px]">{children}</div>
    </CardContent>
  </Card>
);

const SupplierScorecard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [score, setScore] = useState(null);
  const [pos, setPos] = useState([]);
  const [grns, setGrns] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [scoreRes, metricsRes] = await Promise.all([
        axios.get(`${API_URL}/api/procurement/suppliers/score/${id}`, auth),
        axios.get(`${API_URL}/api/procurement/suppliers/score/metrics/${id}`, auth),
      ]);
      setScore(scoreRes.data);
      setPos((metricsRes.data?.pos || []).slice(0, 20));
      setGrns((metricsRes.data?.grns || []).slice(0, 20));
      setInvoices((metricsRes.data?.invoices || []).slice(0, 20));
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load scorecard');
    } finally {
      setLoading(false);
    }
  }, [auth, id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Derived chart data
  const radarData = useMemo(() => {
    if (!score) return [];
    const m = score.metrics;
    return [
      { axis: 'Delivery Accuracy', score: m.delivery_accuracy_pct, full: 100 },
      { axis: 'On-Time', score: m.on_time_delivery_pct, full: 100 },
      { axis: 'Rejection (inv)', score: Math.max(0, 100 - m.rejection_rate_pct), full: 100 },
      { axis: 'Lead Time', score: m.lead_time_score, full: 100 },
      { axis: 'Spend Rank', score: m.spend_score, full: 100 },
      { axis: 'Invoice Rel.', score: m.invoice_payment_reliability_pct, full: 100 },
    ];
  }, [score]);

  // Lead time trend per posted GRN (chronological)
  const leadTimeTrend = useMemo(() => {
    const posted = (grns || [])
      .filter((g) => ['received', 'partial', 'rejected'].includes(g.status))
      .filter((g) => (g.po_snapshot || {}).expected_delivery_date && g.received_date);
    return posted
      .slice()
      .reverse()
      .map((g) => {
        const expected = new Date(g.po_snapshot.expected_delivery_date);
        const received = new Date(g.received_date);
        return {
          label: g.grn_number,
          days: Number(((received - expected) / 86400000).toFixed(1)),
          receivedAt: received.toISOString().slice(0, 10),
        };
      });
  }, [grns]);

  // Spend trend per PO in chronological order
  const spendTrend = useMemo(() => {
    return (pos || [])
      .slice()
      .reverse()
      .filter((p) => p.status !== 'draft')
      .map((p) => ({
        label: p.po_number,
        total: Number(p.total_amount || 0),
        currency: p.currency || 'AED',
      }));
  }, [pos]);

  // Scatter: per-GRN accuracy % vs on-time (days late)
  const scatterData = useMemo(() => {
    return (grns || [])
      .filter((g) => ['received', 'partial', 'rejected'].includes(g.status))
      .filter((g) => (g.po_snapshot || {}).expected_delivery_date && g.received_date)
      .map((g) => {
        const ordered = (g.items || []).reduce((s, i) => s + Number(i.qty_ordered || 0), 0);
        const diff = (g.items || []).reduce((s, i) => s + Math.abs(Number(i.qty_received || 0) - Number(i.qty_ordered || 0)), 0);
        const accuracy = ordered > 0 ? Math.max(0, Math.min(100, (1 - diff / ordered) * 100)) : 0;
        const expected = new Date((g.po_snapshot || {}).expected_delivery_date);
        const received = new Date(g.received_date);
        const daysLate = Number(((received - expected) / 86400000).toFixed(1));
        return { grn: g.grn_number, accuracy: Number(accuracy.toFixed(2)), daysLate };
      });
  }, [grns]);

  // ------ Render ------

  if (loading) {
    return (
      <div className="p-6">
        <BackButton />
        <div className="mt-6 text-center text-slate-500">Loading scorecard…</div>
      </div>
    );
  }

  if (error || !score) {
    return (
      <div className="p-6">
        <BackButton />
        <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error || 'Supplier not found'}
        </div>
      </div>
    );
  }

  const grade = GRADE_COLOURS[score.grade] || GRADE_COLOURS.E;

  return (
    <div className="p-6 space-y-4" data-testid="supplier-scorecard-page">
      <BackButton />

      {/* Header */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Supplier Performance Score</p>
              <h1 className="mt-1 text-2xl font-bold text-slate-900 truncate" data-testid="scorecard-supplier-name">
                {score.supplier_name}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                  {score.category}
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${RISK_COLOURS[score.risk_level]}`}
                  data-testid="scorecard-risk"
                >
                  Risk: {score.risk_level}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/admin/procurement/suppliers`)}
                  data-testid="scorecard-open-supplier"
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Supplier profile
                </Button>
              </div>
            </div>

            {/* Score badge */}
            <div
              className="flex flex-col items-center justify-center rounded-xl px-6 py-3 shadow-md"
              style={{ background: `linear-gradient(135deg, ${grade.bg}, ${grade.bg}EE)` }}
              data-testid="scorecard-score-badge"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: grade.text, opacity: 0.8 }}>
                Score
              </p>
              <p className="text-4xl font-bold leading-tight" style={{ color: grade.text }} data-testid="scorecard-score-value">
                {score.composite_score.toFixed(1)}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <Award className="h-4 w-4" style={{ color: grade.text }} />
                <span className="text-sm font-bold" style={{ color: grade.text }} data-testid="scorecard-grade">
                  Grade {score.grade}
                </span>
              </div>
              <p className="text-[10px] mt-1" style={{ color: grade.text, opacity: 0.85 }}>{grade.label}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI grid 3x2 */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KPICard
          icon={CheckCircle2}
          label="Delivery Accuracy"
          value={`${score.metrics.delivery_accuracy_pct}%`}
          sublabel={`${score.metrics.grn_sample_size} GRN(s) analysed`}
          color="#023E8A"
          testId="kpi-delivery-accuracy"
        />
        <KPICard
          icon={Clock}
          label="On-Time Delivery"
          value={`${score.metrics.on_time_delivery_pct}%`}
          sublabel={`${score.metrics.on_time_delivery_hits}/${score.metrics.on_time_delivery_sample} on schedule`}
          color="#16A34A"
          testId="kpi-on-time"
        />
        <KPICard
          icon={XCircle}
          label="Rejection Rate"
          value={`${score.metrics.rejection_rate_pct}%`}
          sublabel={`${score.metrics.rejection_count}/${score.metrics.rejection_total} GRNs rejected`}
          color="#DC2626"
          testId="kpi-rejection"
        />
        <KPICard
          icon={TrendingUp}
          label="Avg Lead Time"
          value={`${score.metrics.avg_lead_time_days > 0 ? '+' : ''}${score.metrics.avg_lead_time_days} d`}
          sublabel={score.metrics.avg_lead_time_days > 0 ? 'Late vs expected' : 'On schedule or early'}
          color="#D97706"
          testId="kpi-lead-time"
        />
        <KPICard
          icon={DollarSign}
          label="Total Spend"
          value={fmtSpend(score.metrics.total_spend)}
          sublabel={`${score.metrics.po_count} PO(s)`}
          color="#0F766E"
          testId="kpi-total-spend"
        />
        <KPICard
          icon={FileText}
          label="Invoice Reliability"
          value={`${score.metrics.invoice_payment_reliability_pct}%`}
          sublabel={score.metrics.invoice_reliability_sample > 0
            ? `${score.metrics.invoice_reliability_hits}/${score.metrics.invoice_reliability_sample} on-time`
            : 'No invoices yet'}
          color="#7C3AED"
          testId="kpi-invoice-reliability"
        />
      </div>

      {/* Radar + Lead Time Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="Composite Score Breakdown" testId="chart-radar">
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#CBD5E1" />
              <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: '#475569' }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: '#94A3B8' }} />
              <Radar name={score.supplier_name} dataKey="score" stroke="#023E8A" fill="#023E8A" fillOpacity={0.35} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Lead Time Trend (days vs expected)" testId="chart-lead-time">
          {leadTimeTrend.length === 0 ? (
            <div className="flex items-center justify-center h-[220px] text-slate-400 text-xs">No posted GRNs yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={leadTimeTrend} margin={{ top: 10, right: 15, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748B' }} interval={0} angle={-35} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10, fill: '#64748B' }} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [`${v} d`, 'vs expected']} />
                <Line type="monotone" dataKey="days" stroke="#D97706" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Spend Trend + Scatter */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="PO Spend Trend" testId="chart-spend-trend">
          {spendTrend.length === 0 ? (
            <div className="flex items-center justify-center h-[220px] text-slate-400 text-xs">No POs yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={spendTrend} margin={{ top: 10, right: 15, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748B' }} interval={0} angle={-35} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, _n, p) => [`${p?.payload?.currency} ${Number(v).toLocaleString('en-US')}`, 'Total']} />
                <Bar dataKey="total" fill="#023E8A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Delivery Accuracy vs Days Late" testId="chart-scatter">
          {scatterData.length === 0 ? (
            <div className="flex items-center justify-center h-[220px] text-slate-400 text-xs">No posted GRNs yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <ScatterChart margin={{ top: 10, right: 15, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" dataKey="daysLate" name="Days late" tick={{ fontSize: 10, fill: '#64748B' }} label={{ value: 'Days late vs expected', position: 'insideBottom', offset: -5, fontSize: 10, fill: '#475569' }} />
                <YAxis type="number" dataKey="accuracy" name="Accuracy" domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748B' }} label={{ value: 'Accuracy %', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#475569' }} />
                <Tooltip
                  contentStyle={{ fontSize: 12 }}
                  cursor={{ strokeDasharray: '3 3' }}
                  formatter={(value, name, item) => [value, item?.payload?.grn]}
                />
                <Scatter data={scatterData} fill="#FF6B35" />
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Insights */}
      <Card data-testid="insights-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            <h3 className="text-sm font-semibold text-slate-800">Automatic Insights</h3>
          </div>
          {score.insights.length === 0 ? (
            <p className="text-sm text-slate-500">No insights available — insufficient data.</p>
          ) : (
            <ul className="space-y-2">
              {score.insights.map((ins, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`insight-${i}`}>
                  <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                  <span>{ins}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* History tables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card data-testid="table-pos">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1">
                <Package className="h-4 w-4" />
                Last POs
              </h3>
              <span className="text-xs text-slate-500">{pos.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="py-1 font-semibold">Number</th>
                    <th className="py-1 font-semibold">Status</th>
                    <th className="py-1 font-semibold text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {pos.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/procurement/purchase-orders/${p.id}`)}>
                      <td className="py-1.5 font-medium text-slate-800 truncate">{p.po_number}</td>
                      <td className="py-1.5 text-slate-600">{p.status}</td>
                      <td className="py-1.5 text-right font-medium">{p.currency} {Number(p.total_amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {pos.length === 0 && (
                    <tr><td colSpan="3" className="py-3 text-center text-slate-400">No POs</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="table-grns">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4" />
                Last GRNs
              </h3>
              <span className="text-xs text-slate-500">{grns.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="py-1 font-semibold">Number</th>
                    <th className="py-1 font-semibold">Status</th>
                    <th className="py-1 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {grns.map((g) => (
                    <tr key={g.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/procurement/grns/${g.id}`)}>
                      <td className="py-1.5 font-medium text-slate-800 truncate">{g.grn_number}</td>
                      <td className="py-1.5 text-slate-600">{g.status}</td>
                      <td className="py-1.5 text-slate-500">{(g.received_date || '').slice(0, 10)}</td>
                    </tr>
                  ))}
                  {grns.length === 0 && (
                    <tr><td colSpan="3" className="py-3 text-center text-slate-400">No GRNs</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="table-invoices">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1">
                <FileText className="h-4 w-4" />
                Last Invoices
              </h3>
              <span className="text-xs text-slate-500">{invoices.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="py-1 font-semibold">Number</th>
                    <th className="py-1 font-semibold">Status</th>
                    <th className="py-1 font-semibold text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/procurement/invoices/${inv.id}`)}>
                      <td className="py-1.5 font-medium text-slate-800 truncate">{inv.invoice_number}</td>
                      <td className="py-1.5 text-slate-600">{inv.status}</td>
                      <td className="py-1.5 text-right font-medium">{inv.currency} {Number(inv.total_amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {invoices.length === 0 && (
                    <tr><td colSpan="3" className="py-3 text-center text-slate-400">No invoices</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="text-center text-[10px] text-slate-400">
        Generated at {score.generated_at ? new Date(score.generated_at).toLocaleString() : '—'}
      </p>
    </div>
  );
};

export default SupplierScorecard;
